import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Building,
  Check,
  ChevronRight,
  CreditCard,
  Crown,
  Database,
  Globe,
  Key,
  LayoutGrid,
  List,
  Loader2,
  LogOut,
  Mail,
  Menu,
  Settings as SettingsIcon,
  Trash2,
  User,
  Wifi,
  X,
  Zap
} from 'lucide-react';
import { cn } from '@foundation/lib/utils';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@foundation/components/ui/popover';
import { OfflineConfigDiagnostic } from '@foundation/components/debug/OfflineConfigDiagnostic';
import { ProfileEditingDebug } from '@foundation/components/debug/ProfileEditingDebug';
import { UserPermissionsDiagnostic } from '@foundation/components/debug/UserPermissionsDiagnostic';
import { OfflineFirstInvitationNotifications } from '@foundation/components/invitation/OfflineFirstInvitationNotifications';
import { CreateOrganizationDialog } from '@foundation/components/organization/CreateOrganizationDialog';
import { ApiKeysSection } from '@foundation/components/settings/ApiKeysSection';
import { DomainsSection } from '@foundation/components/settings/DomainsSection';
import IntegrationsSection from '@foundation/components/settings/IntegrationsSection';
import { InvitationsSettingsTab } from '@foundation/components/settings/InvitationsSettingsTab';
import { WebhooksSection } from '@foundation/components/settings/WebhooksSection';
import { PhoneInput } from '@foundation/components/ui/phone-input';
import { useUser } from '@foundation/contextapi/UserContext';
import { useToast } from '@foundation/hooks/use-toast';
import { useUserPermissions } from '@foundation/hooks/useUserPermissions';
import {
  getUserOrganizations,
  leaveOrganization,
} from '@foundation/services/organizationService';
import {
  addSubscriptionAddon,
  getAvailableAddonPacks,
  getAvailablePlans,
  getOrganizationSubscription,
  getResourceTypes,
  getResourceUsage,
  getSubscriptionAddons,
  getUserSubscription,
} from '@foundation/services/subscriptionService';
import { getUserTeams, TeamWithDetails } from '@foundation/services/teamService';
import type { ResourceType, ResourceUsage } from '@foundation/types/subscription';
import { logFeatureFlags, settingsFeatureFlags } from '@foundation/utils/featureFlags';
import { PhoneValidationResult } from '@foundation/utils/phoneValidation';
import { EnhancedProfileSettings } from './EnhancedProfileSettings';

// Check if CRM Base is enabled via environment variable
const isCrmBaseEnabled = import.meta.env.VITE_CRM_BASE_ENABLED === 'true';

// Debug logging
console.log('Environment check:', {
  VITE_CRM_BASE_ENABLED: import.meta.env.VITE_CRM_BASE_ENABLED,
  isCrmBaseEnabled,
  allEnvVars: import.meta.env,
});

// Create a fallback component for when CRM is not available
const DataModelNotAvailable: React.FC = () => (
  <div className='dashboard-card p-6'>
    <h3 className='text-xl font-semibold mb-4'>Data Model Settings</h3>
    <div className='text-center py-8'>
      <Database className='w-12 h-12 mx-auto mb-4 opacity-50' />
      <p className='text-muted-foreground mb-2'>
        {isCrmBaseEnabled
          ? 'Data Model settings are not available. The CRM module may not be installed.'
          : 'CRM Base is not enabled. Data Model settings are not available.'}
      </p>
      <p className='text-sm text-muted-foreground'>
        {isCrmBaseEnabled
          ? 'Please ensure the CRM Base module is properly installed.'
          : 'Set VITE_CRM_BASE_ENABLED=true in your environment to enable CRM features.'}
      </p>
    </div>
  </div>
);

// Dynamically import DataModelSettings only when needed
const loadDataModelSettings = async () => {
  console.log(
    'loadDataModelSettings called, isCrmBaseEnabled:',
    isCrmBaseEnabled
  );

  if (!isCrmBaseEnabled) {
    console.log('CRM Base not enabled, returning fallback component');
    return { default: DataModelNotAvailable };
  }

  try {
    // Use dynamic path construction to avoid Vite static analysis
    const basePath = '../../../../../features/crm_base/pages/settings/';
    const componentName = 'DataModelSettings';
    const fullPath = basePath + componentName;

    console.log('Attempting to import from path:', fullPath);
    const module = await import(/* @vite-ignore */ fullPath);
    console.log('Successfully imported DataModelSettings:', module);
    return module;
  } catch (error) {
    console.error('CRM Base DataModelSettings not found:', error);
    return { default: DataModelNotAvailable };
  }
};

const DataModelSettings = React.lazy(loadDataModelSettings);

type SettingsTab =
  | 'profile'
  | 'organizations'
  | 'invitations'
  | 'subscription'
  | 'api-keys'
  | 'webhooks'
  | 'integrations'
  | 'domains'
  | 'data-model'
  | 'debug'
  | 'debug-offline'
  | 'debug-profile';

interface Organization {
  id: string;
  name: string;
  description: string | null;
  tenant_type?: 'personal' | 'organization';
  created_at: string;
  created_by: string;
  updated_at: string;
  role: string;
}

interface Plan {
  id: string;
  name: string;
  description?: string;
  tier_level: number;
  monthly_price_id?: string;
  yearly_price_id?: string;
  price_monthly?: number;
  price_yearly?: number;
  features: any;
}

interface Subscription {
  id: string;
  owner_id: string;
  owner_type: string;
  plan_id: string;
  status: string;
  billing_cycle: string;
  current_period_start?: string;
  current_period_end?: string;
  plan?: Plan;
}

interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: string;
  invoice_date: string;
  due_date?: string;
  paid_date?: string;
}

const SETTINGS_TAB_KEY = 'revisr_settings_active_tab';

const Settings: React.FC = () => {
  const location = useLocation();
  // Priority: navigation state (programmatic) > sessionStorage (reload) > default
  const initialTab = (location.state as { tab?: SettingsTab } | null)?.tab
    ?? (sessionStorage.getItem(SETTINGS_TAB_KEY) as SettingsTab | null)
    ?? 'profile';
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { permissions } = useUserPermissions();

  const handleTabClick = (tabId: SettingsTab) => {
    setActiveTab(tabId);
    setMobileOpen(false);
  };
  
  // Persist active tab to sessionStorage on every change
  useEffect(() => {
    sessionStorage.setItem(SETTINGS_TAB_KEY, activeTab);
  }, [activeTab]);

  // Log feature flags on component mount for debugging
  useEffect(() => {
    logFeatureFlags();
  }, []);

  // Filter tabs based on permissions and feature flags
  const getAvailableTabs = () => {
    const allTabs = [
      {
        id: 'profile' as SettingsTab,
        label: 'Profile',
        icon: User,
        description: 'Manage your personal information',
        requiresPermission: () => true, // Everyone can access profile
      },
      {
        id: 'organizations' as SettingsTab,
        label: 'Organisation',
        icon: Building,
        description: 'Manage your organisations',
        requiresPermission: () => true, //!permissions?.isGuest,
      },
      {
        id: 'invitations' as SettingsTab,
        label: 'Invitations',
        icon: Mail,
        description: 'View and respond to pending invitations',
        requiresPermission: () => true,
      },
      {
        id: 'subscription' as SettingsTab,
        label: 'Subscription',
        icon: CreditCard,
        description: 'Billing and subscription management',
        requiresPermission: () => true,
      },
      {
        id: 'api-keys' as SettingsTab,
        label: 'API Keys',
        icon: Key,
        description: 'Manage API keys for programmatic access',
        requiresPermission: () =>
          settingsFeatureFlags.apiKeys && !permissions?.isGuest,
      },
      {
        id: 'webhooks' as SettingsTab,
        label: 'Webhooks',
        icon: Zap,
        description: 'Configure webhooks for event notifications',
        requiresPermission: () =>
          settingsFeatureFlags.webhooks && !permissions?.isGuest,
      },
      {
        id: 'integrations' as SettingsTab,
        label: 'Integrations',
        icon: Database,
        description: 'Connect to external databases and services',
        requiresPermission: () =>
          settingsFeatureFlags.integrations && !permissions?.isGuest,
      },
      {
        id: 'domains' as SettingsTab,
        label: 'Domains',
        icon: Globe,
        description: 'Manage subdomain and custom domain settings',
        requiresPermission: () =>
          settingsFeatureFlags.domains && !permissions?.isGuest,
      },
      // {
      //   id: "data-model" as SettingsTab,
      //   label: "Data Model",
      //   icon: Database,
      //   description: "Manage custom objects and data models",
      //   requiresPermission: () => settingsFeatureFlags.dataModel && isCrmBaseEnabled && !permissions?.isGuest,
      // },
      // Debug pages disabled — not required for production
      // {
      //   id: 'debug' as SettingsTab,
      //   label: 'Debug Permissions',
      //   icon: AlertCircle,
      //   description: 'View current user roles and permissions',
      //   requiresPermission: () => true,
      // },
      // {
      //   id: 'debug-offline' as SettingsTab,
      //   label: 'Debug Offline',
      //   icon: Wifi,
      //   description: 'View offline configuration and status',
      //   requiresPermission: () => true,
      // },
      // {
      //   id: 'debug-profile' as SettingsTab,
      //   label: 'Debug Profile',
      //   icon: User,
      //   description: 'Debug profile editing functionality',
      //   requiresPermission: () => true,
      // },
    ];

    return allTabs.filter(tab => tab.requiresPermission());
  };

  const tabs = getAvailableTabs();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <EnhancedProfileSettings />;
      case 'organizations':
        return <OrganizationsSettings />;
      case 'invitations':
        return <InvitationsSettingsTab />;
      case 'subscription':
        return <SubscriptionSettings />;
      case 'api-keys':
        return <ApiKeysSection />;
      case 'webhooks':
        return <WebhooksSection />;
      case 'integrations':
        return <IntegrationsSection />;
      case 'domains':
        return <DomainsSection />;
      case 'data-model':
        return (
          <React.Suspense
            fallback={
              <div className='dashboard-card p-6'>
                <div className='flex items-center justify-center py-8'>
                  <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-primary'></div>
                  <span className='ml-2'>Loading Data Model settings...</span>
                </div>
              </div>
            }
          >
            <DataModelSettings />
          </React.Suspense>
        );
      case 'debug':
        return <UserPermissionsDiagnostic />;
      case 'debug-offline':
        return <OfflineConfigDiagnostic />;
      case 'debug-profile':
        return <ProfileEditingDebug />;
      default:
        return <ProfileSettings />;
    }
  };

  const activeTabLabel = tabs.find(t => t.id === activeTab)?.label ?? 'Settings';

  return (
    <div
      className='flex bg-gradient-to-br from-background via-background to-muted/20 overflow-hidden'
      style={{ height: 'calc(100vh - 3.5rem)' }}
    >
      {/* ── Mobile backdrop ─────────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className='fixed inset-0 z-40 bg-black/50 md:hidden'
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      {/* Mobile: fixed drawer slide-in; Desktop: static 280px panel */}
      <div
        className={cn(
          'flex flex-col overflow-hidden flex-shrink-0',
          'transition-transform duration-300 ease-in-out',
          // Mobile: fixed overlay
          'fixed top-14 bottom-0 left-0 z-50 w-[280px]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop: static in flex layout
          'md:relative md:top-auto md:bottom-auto md:left-auto md:z-auto md:translate-x-0',
        )}
        style={{
          background: '#132321',
          borderRight: '1px solid rgba(51,184,100,0.15)',
          height: '100%',
        }}
      >
        {/* Top accent line */}
        <div style={{ height: '2px', background: 'linear-gradient(90deg, #33b864, transparent)', flexShrink: 0 }} />

        {/* Sidebar header */}
        <div className='px-4 py-4 flex items-center justify-between' style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          <div className='flex items-center gap-2.5'>
            <div
              className='w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0'
              style={{ background: 'rgba(51,184,100,0.15)' }}
            >
              <SettingsIcon className='w-4 h-4' style={{ color: '#33b864' }} />
            </div>
            <div>
              <p className='text-sm font-bold' style={{ color: '#ffffff', letterSpacing: '-0.01em' }}>Settings</p>
              <p className='text-[11px]' style={{ color: 'rgba(255,255,255,0.4)' }}>Account & preferences</p>
            </div>
          </div>
          {/* Mobile close button */}
          <button
            className='md:hidden p-1.5 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center'
            style={{ background: 'transparent' }}
            onClick={() => setMobileOpen(false)}
            aria-label='Close settings navigation'
          >
            <X className='w-5 h-5' style={{ color: 'rgba(255,255,255,0.6)' }} />
          </button>
        </div>

        {/* Nav items */}
        <nav className='p-2.5 space-y-0.5' style={{ flex: 1, overflowY: 'auto' }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className='w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 relative flex items-center gap-3 min-h-[44px]'
                style={{
                  background: isActive ? 'rgba(51,184,100,0.14)' : 'transparent',
                  color: isActive ? '#33b864' : 'rgba(255,255,255,0.72)',
                }}
                onMouseEnter={e => {
                  if (!isActive) {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)';
                    (e.currentTarget as HTMLElement).style.color = '#ffffff';
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive) {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                    (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.72)';
                  }
                }}
              >
                {isActive && (
                  <div
                    className='absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full'
                    style={{ background: '#33b864' }}
                  />
                )}
                <Icon
                  className='w-4 h-4 flex-shrink-0'
                  style={{ color: isActive ? '#33b864' : 'rgba(255,255,255,0.45)' }}
                />
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium'>{tab.label}</p>
                  <p className='text-[11px] truncate' style={{ color: isActive ? 'rgba(51,184,100,0.7)' : 'rgba(255,255,255,0.35)' }}>
                    {tab.description}
                  </p>
                </div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── Main content ────────────────────────────────────────────── */}
      <div className='flex flex-col flex-1 min-w-0 overflow-hidden'>
        {/* Mobile top bar — hamburger + current section name */}
        <div className='md:hidden flex items-center gap-3 px-4 h-11 bg-white border-b border-zinc-100 flex-shrink-0'>
          <button
            onClick={() => setMobileOpen(true)}
            className='p-1.5 rounded-lg text-zinc-600 hover:bg-zinc-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center'
            aria-label='Open settings navigation'
          >
            <Menu className='h-5 w-5' />
          </button>
          <div className='flex items-center gap-2 min-w-0'>
            <SettingsIcon className='h-4 w-4 text-zinc-400 flex-shrink-0' />
            <span className='text-sm font-semibold text-zinc-800 truncate'>{activeTabLabel}</span>
          </div>
        </div>

        <div className='flex-1 overflow-y-auto'>
          <OfflineFirstInvitationNotifications />
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className='p-4 md:p-8'
          >
            <AnimatePresence mode='wait'>
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderTabContent()}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// Profile Settings Component
const ProfileSettings: React.FC = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.user_metadata?.name || '',
    fullName: user?.user_metadata?.full_name || '',
    phone: user?.phone || '',
  });
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidationResult>(
    {
      isValid: true,
      formatted: '',
    }
  );

  // Load profile data from profiles table
  const loadProfileData = async () => {
    if (!user?.id) return;

    try {
      const { supabase } = await import('@foundation/utils/supabaseClient');

      // Try to get profile data from profiles table first
      const { data: profileData, error } = await supabase.rpc(
        'get_user_profile',
        { user_id: user.id }
      );

      if (!error && profileData && profileData.length > 0) {
        // Use profile data from profiles table (RPC returns array)
        const profile = profileData[0];
        setFormData({
          name: profile.username || '',
          fullName: profile.full_name || '',
          phone: profile.phone || '',
        });
      } else {
        // Fallback to auth user metadata if profiles table doesn't have data
        setFormData({
          name: user.user_metadata?.name || '',
          fullName: user.user_metadata?.full_name || '',
          phone: user.phone || '',
        });
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
      // Fallback to auth user metadata
      setFormData({
        name: user.user_metadata?.name || '',
        fullName: user.user_metadata?.full_name || '',
        phone: user.phone || '',
      });
    }
  };

  // Load profile data when user changes
  // useEffect(() => {
  //   if (user?.id) {
  //     loadProfileData();
  //   }
  // }, [user?.id]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);

      // Validate phone number if provided
      if (formData.phone && !phoneValidation.isValid) {
        toast({
          title: 'Validation Error',
          description:
            'Please enter a valid phone number in international format.',
          variant: 'destructive',
        });
        return;
      }

      // Import supabase client
      const { supabase } = await import('@foundation/utils/supabaseClient');

      // Update the profiles table with all profile information including phone
      const { error: profileError } = await supabase.rpc(
        'update_user_profile',
        {
          user_id: user?.id,
          profile_name: formData.name,
          profile_full_name: formData.fullName,
          profile_phone: formData.phone || null, // Use null if phone is empty
        }
      );

      if (profileError) {
        // Handle specific phone validation errors from Supabase
        if (
          profileError.message?.includes('Invalid phone number format') ||
          profileError.message?.includes('E.164')
        ) {
          toast({
            title: 'Phone Number Error',
            description:
              'Phone number must be in international format (e.g., +1234567890). Please check your phone number and try again.',
            variant: 'destructive',
          });
          return;
        }

        console.error('Profile update failed:', profileError);
        throw profileError;
      }

      // Only update auth metadata (not phone) for basic user info
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          name: formData.name,
          full_name: formData.fullName,
        },
      });

      // Don't throw error if auth metadata update fails - profiles table is the primary source
      if (authError) {
        console.warn('Auth metadata update failed:', authError);
      }

      // Reload profile data to reflect changes
      // await loadProfileData();

      toast({
        title: 'Profile updated',
        description:
          'Your profile information has been saved successfully to your profile.',
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update profile information. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='space-y-6'>
      <div className='dashboard-card p-6'>
        <h3 className='text-xl font-semibold mb-4'>Profile Information</h3>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <div>
            <label className='block text-sm font-medium mb-2'>Full Name</label>
            <input
              type='text'
              className='w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50'
              placeholder='Enter your full name'
              value={formData.fullName}
              onChange={e => handleInputChange('fullName', e.target.value)}
            />
          </div>
          <div>
            <label className='block text-sm font-medium mb-2'>Email</label>
            <input
              type='email'
              className='w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 bg-muted/50'
              placeholder='Enter your email'
              value={user?.email || ''}
              disabled
            />
            <p className='text-xs text-muted-foreground mt-1'>
              Email cannot be changed from here. Contact support if needed.
            </p>
          </div>
          <div>
            <label className='block text-sm font-medium mb-2'>Phone</label>
            <PhoneInput
              value={formData.phone}
              onChange={(formatted, validation) => {
                setPhoneValidation(validation);
                handleInputChange('phone', formatted);
              }}
              placeholder='Enter your phone number'
              className='w-full'
            />
          </div>
          {/* <div>
            <label className="block text-sm font-medium mb-2">Location</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="Enter your location"
              value={formData.location}
              onChange={(e) => handleInputChange("location", e.target.value)}
            />
          </div> */}
        </div>
        <div className='mt-6 flex justify-end'>
          <button
            onClick={handleSaveProfile}
            disabled={loading}
            className='px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2'
          >
            {loading && <Loader2 className='w-4 h-4 animate-spin' />}
            <span>{loading ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Organizations Settings Component
const OrganizationsSettings: React.FC = () => {
  const { user, session } = useUser();
  const { toast } = useToast();
  const { permissions } = useUserPermissions();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [teams, setTeams] = useState<TeamWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});
  const [logoUrls, setLogoUrls] = useState<Record<string, string | null>>({});
  const [orgSearch, setOrgSearch] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const PAGE_SIZE = 9;
  const LIST_PAGE_SIZE = 8;
  const [orgPage, setOrgPage] = useState(1);
  const [confirmLeave, setConfirmLeave] = useState<{
    orgId: string;
    orgName: string;
    tenantType?: 'personal' | 'organization';
  } | null>(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id]);

  // Refresh a single org's logo immediately when branding is updated
  useEffect(() => {
    const handler = async (e: Event) => {
      const { orgId } = (e as CustomEvent<{ orgId: string }>).detail ?? {};
      if (!orgId) return;
      try {
        const { BrandingService } = await import('../services/brandingService');
        const branding = await BrandingService.getOrganizationBranding(orgId);
        const url = branding?.logo_url
          ? BrandingService.getLogoUrlWithCacheBusting(branding.logo_url)
          : null;
        setLogoUrls(prev => ({ ...prev, [orgId]: url }));
      } catch {
        // keep existing
      }
    };
    window.addEventListener('branding-updated', handler);
    return () => window.removeEventListener('branding-updated', handler);
  }, []);

  // After any workspace is created from Settings, ensure its subscription + resource_usage rows exist.
  // createOrganization() goes through the complete_onboarding_with_workspace RPC which does NOT
  // create a subscription — this call fills that gap idempotently.
  const handleWorkspaceCreated = async (orgId: string) => {
    try {
      const apiBase = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';
      await fetch(`${apiBase}/api/revisr/subscription/ensure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId }),
      });
    } catch (err) {
      console.warn('[Settings] Failed to ensure subscription for new workspace:', err);
    }
    loadData();
  };

  const loadData = async () => {
    try {
      setLoading(true);

      // Load organizations
      console.log('[Settings] 🔍 Loading organizations...');
      const orgData = await getUserOrganizations();
      console.log('[Settings] ✅ Organizations loaded:', orgData);
      console.log('[Settings] 📊 Organizations data:', {
        count: orgData.organizations.length,
        organizations: orgData.organizations.map(org => ({
          id: org.id,
          name: org.name,
          tenant_type: org.tenant_type,
        })),
        membership: orgData.membership,
      });

      const mappedOrgs = orgData.organizations.map(org => {
        const membership = orgData.membership.find(
          m => m.organisation_id === org.id
        );
        return {
          id: org.id,
          name: org.name,
          description: org.description,
          tenant_type: org.tenant_type,
          created_at: org.created_at,
          created_by: org.created_by,
          updated_at: org.updated_at,
          role: membership?.role || 'member',
        };
      });

      console.log('[Settings] 📋 Mapped organizations:', mappedOrgs);
      setOrganizations(mappedOrgs);

      // Fetch member counts for all orgs in parallel
      // @ts-ignore – alias resolved by Vite; same pattern used in confirmLeaveOrganization
      const { supabase: sbClient } = await import('@foundation/utils/supabaseClient');
      const counts: Array<{ id: string; count: number }> = await Promise.all(
        mappedOrgs.map(async (org: Organization) => {
          const { count } = await sbClient
            .from('base_organisation_members')
            .select('*', { count: 'exact', head: true })
            .eq('organisation_id', org.id)
            .eq('is_active', true);
          return { id: org.id, count: count ?? 0 };
        })
      );
      setMemberCounts(Object.fromEntries(counts.map((c: { id: string; count: number }) => [c.id, c.count])));

      // Fetch logos for all orgs in parallel — apply cache-busting so re-uploads
      // (same file path) show the new image without requiring a page refresh.
      const { BrandingService } = await import('../services/brandingService');
      const logoEntries = await Promise.all(
        mappedOrgs.map(async (org: Organization) => {
          try {
            const branding = await BrandingService.getOrganizationBranding(org.id);
            const url = branding?.logo_url
              ? BrandingService.getLogoUrlWithCacheBusting(branding.logo_url)
              : null;
            return { id: org.id, url };
          } catch {
            return { id: org.id, url: null };
          }
        })
      );
      setLogoUrls(Object.fromEntries(logoEntries.map((e: { id: string; url: string | null }) => [e.id, e.url])));

      // Load teams
      const teamData = await getUserTeams();
      setTeams(teamData);
    } catch (error) {
      console.error('Error loading organizations and teams:', error);
      toast({
        title: 'Error',
        description: 'Failed to load organizations and teams',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveOrganization = async (orgId: string, orgName: string) => {
    // Find the organization to get its tenant_type
    const org = organizations.find(o => o.id === orgId);
    // Invariant: a user must always be linked to at least one workspace.
    // Block leave/delete when this is the only workspace, even if the disabled
    // button is bypassed (e.g. via keyboard or programmatic click).
    // Gated on `!loading` so a programmatic call during the initial fetch
    // (when `organizations` is still empty) doesn't falsely short-circuit.
    if (!loading && organizations.length <= 1) {
      console.warn('[Settings] Leave/delete invoked while user has only one workspace; short-circuiting.');
      toast({
        title: org?.tenant_type === 'personal' ? 'Cannot Delete Workspace' : 'Cannot Leave Organisation',
        description: 'This is your only workspace. Create or join another workspace before leaving or deleting it.',
        variant: 'destructive',
      });
      return;
    }
    setConfirmLeave({
      orgId,
      orgName,
      tenantType: org?.tenant_type === 'personal' ? 'personal' : 'organization',
    });
  };

  /**
   * Ask the server to delete this tenant's Teable **bases** before the
   * destructive RPC removes the base_tenants row (and the JSONB `bases`
   * column with it). The shared Teable **space** is NOT touched here —
   * deleting it would wipe every other tenant's data.
   *
   * Mirrors what AccountDeletionModal does and what the org-manage page's
   * server route already does — without this step, deleting a personal
   * workspace or the last-member-org from the Settings list orphans this
   * tenant's bases because the RPC path bypasses
   * /api/revisr/organisation/:id.
   *
   * Fire-and-forget from the caller's perspective: failures are logged but
   * never block the app-side delete.
   */
  const cleanupTeableBasesForTenant = async (tenantId: string): Promise<void> => {
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: import.meta.env is provided by Vite at build time
      const apiUrl: string = (import.meta.env?.VITE_API_URL as string) || 'http://localhost:3001';

      const accessToken = session?.access_token;
      if (!accessToken) {
        console.warn(
          '[Settings][TeableBaseDelete] ⚠️  No access token — cannot call cleanup endpoint. Bases will remain in Teable.'
        );
        return;
      }

      // The server route reads base_tenants.bases for the tenant and
      // iterates each base id, calling DELETE /api/base/:baseId on Teable.
      const response = await fetch(`${apiUrl}/api/teable/bases/tenant/${tenantId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        console.error(
          `[Settings][TeableBaseDelete] ❌ Cleanup endpoint returned ${response.status}:`,
          errBody
        );
        return;
      }

      console.log(
        `[Settings][TeableBaseDelete] ✅ Teable bases for tenant ${tenantId} cleanup requested`
      );
    } catch (error) {
      console.error(
        '[Settings][TeableBaseDelete] ❌ Unexpected error during cleanup:',
        error instanceof Error ? error.message : error
      );
    }
  };

  const confirmLeaveOrganization = async () => {
    if (!confirmLeave) return;

    try {
      setLeaving(true);
      const isPersonal = confirmLeave.tenantType === 'personal';
      console.log(
        `[Settings] 🚪 ${isPersonal ? 'Deleting personal workspace' : 'Leaving organization'}:`,
        confirmLeave
      );

      const { supabase } = await import('@foundation/utils/supabaseClient');

      if (isPersonal) {
        // Personal workspace deletion logic
        const totalUserOrgs = organizations.length;
        const personalWorkspacesCount = organizations.filter(
          org => org.tenant_type === 'personal'
        ).length;
        console.log(
          `[Settings] User has ${totalUserOrgs} total workspaces, ${personalWorkspacesCount} personal`
        );

        if (totalUserOrgs <= 1 || personalWorkspacesCount <= 1) {
          toast({
            title: 'Cannot Delete Workspace',
            description:
              'You cannot delete your only workspace. Please create another workspace first.',
            variant: 'destructive',
          });
          return;
        }

        // Teable cleanup MUST run before the RPC removes base_tenants. The
        // RPC cascades the row (and the JSONB `bases` column with it);
        // running this after would orphan every base owned by this tenant.
        // The shared Teable space lives on. Helper is fire-and-forget on
        // the server side.
        await cleanupTeableBasesForTenant(confirmLeave.orgId);

        // Delete personal workspace using delete_workspace_with_cleanup
        console.log('[Settings] 🗑️ Deleting personal workspace');
        const { data: deleteResult, error: deleteError } = await supabase.rpc(
          'delete_workspace_with_cleanup',
          {
            p_tenant_id: confirmLeave.orgId,
            p_user_id: user?.id,
          }
        );

        if (deleteError) {
          console.error('Error deleting personal workspace:', deleteError);
          throw deleteError;
        }

        console.log('Personal workspace deletion result:', deleteResult);

        if (deleteResult?.success) {
          toast({
            title: 'Personal Workspace Deleted',
            description: `${confirmLeave.orgName} has been permanently deleted.`,
          });
        } else {
          throw new Error(
            deleteResult?.error || 'Personal workspace deletion failed'
          );
        }
      } else {
        // Organization leave logic
        // Get member count for this organization
        const { count: memberCount, error: countError } = await supabase
          .from('base_organisation_members')
          .select('*', { count: 'exact', head: true })
          .eq('organisation_id', confirmLeave.orgId)
          .eq('is_active', true);

        if (countError) {
          console.error('Error checking member count:', countError);
          throw countError;
        }

        const totalMembers = memberCount || 0;
        console.log(
          `[Settings] Organization has ${totalMembers} active members`
        );

        // Check if user has only 1 organization total (use the same data source as the UI)
        const totalUserOrgs = organizations.length;
        console.log(`[Settings] User has ${totalUserOrgs} total organizations`);

        if (totalUserOrgs <= 1) {
          // User has only 1 organization - prevent deletion
          toast({
            title: 'Cannot Delete Organization',
            description:
              'You cannot delete your only organization. Please create another organization first.',
            variant: 'destructive',
          });
          return;
        }

        if (totalMembers <= 1) {
          // User is the last member - delete the organization instead
          console.log(
            '[Settings] 🗑️ User is last member, deleting organization'
          );

          // Teable cleanup MUST run before either RPC removes base_tenants.
          // Both delete_organization_comprehensive and the fallback
          // delete_workspace_with_cleanup cascade the tenant row (and the
          // JSONB `bases` column with it). The shared Teable space is
          // NEVER deleted here. See cleanupTeableBasesForTenant for the
          // full failure-handling rationale.
          await cleanupTeableBasesForTenant(confirmLeave.orgId);

          // Try comprehensive deletion first, fall back to workspace cleanup
          // (handles tenant_type spelling mismatch: 'organisation' vs 'organization')
          const { data: deleteResult, error: deleteError } = await supabase.rpc(
            'delete_organization_comprehensive',
            {
              p_org_id: confirmLeave.orgId,
              p_user_id: user?.id,
            }
          );

          if (deleteError) {
            console.error('Error deleting organization:', deleteError);
            throw deleteError;
          }

          console.log('Organization deletion result:', deleteResult);

          if (deleteResult?.success && deleteResult?.deleted) {
            toast({
              title: 'Organization Deleted',
              description: `You were the last member of ${confirmLeave.orgName}. The organization has been deleted.`,
            });
          } else {
            // Fallback: delete_workspace_with_cleanup works for any tenant_type
            console.log('[Settings] Comprehensive delete returned false, trying workspace cleanup fallback');
            const { data: wsResult, error: wsError } = await supabase.rpc(
              'delete_workspace_with_cleanup',
              {
                p_tenant_id: confirmLeave.orgId,
                p_user_id: user?.id,
              }
            );

            if (wsError) {
              console.error('Error in workspace cleanup fallback:', wsError);
              throw wsError;
            }

            if (wsResult?.success) {
              toast({
                title: 'Organization Deleted',
                description: `You were the last member of ${confirmLeave.orgName}. The organization has been deleted.`,
              });
            } else {
              throw new Error(
                wsResult?.error || 'Organization deletion failed'
              );
            }
          }
        } else {
          // Normal leave process
          await leaveOrganization(confirmLeave.orgId);

          toast({
            title: 'Success',
            description: `You have successfully left ${confirmLeave.orgName}`,
          });
        }
      }

      // Remove immediately from local state, then re-fetch to confirm server state
      setOrganizations(prev => prev.filter(o => o.id !== confirmLeave.orgId));
      loadData();
    } catch (error) {
      console.error('Error leaving/deleting:', error);
      const isPersonal = confirmLeave.tenantType === 'personal';
      toast({
        title: 'Error',
        description: `Failed to ${isPersonal ? 'delete personal workspace' : 'leave organization'}. Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setLeaving(false);
      setConfirmLeave(null);
    }
  };

  if (loading) {
    return (
      <div className='dashboard-card p-6'>
        <div className='flex items-center justify-center py-8'>
          <Loader2 className='w-6 h-6 animate-spin' />
          <span className='ml-2'>Loading organisations...</span>
        </div>
      </div>
    );
  }

  // Filter organizations by type
  // Debug: Log all tenant types to see what we're working with
  console.log(
    '[Settings] 🏢 All organisations:',
    organizations.map(org => ({
      id: org.id,
      name: org.name,
      tenant_type: org.tenant_type,
    }))
  );

  const personalWorkspaces = organizations.filter(
    org => org.tenant_type === 'personal'
  );
  // tenant_type is normalized in the service, so we only need to check for 'organization'
  const organizationWorkspaces = organizations.filter(org => {
    const isOrg = org.tenant_type === 'organization';
    if (!isOrg) {
      console.log(
        `[Settings] ⚠️ Organisation "${org.name}" filtered out: tenant_type="${org.tenant_type}"`
      );
    }
    return isOrg;
  });

  console.log('[Settings] 📊 Filtered results:', {
    totalOrganizations: organizations.length,
    personalCount: personalWorkspaces.length,
    organizationCount: organizationWorkspaces.length,
    personalWorkspaces: personalWorkspaces.map(org => ({
      name: org.name,
      type: org.tenant_type,
    })),
    organizationWorkspaces: organizationWorkspaces.map(org => ({
      name: org.name,
      type: org.tenant_type,
    })),
  });

  // Helpers for new card+list design
  const AVATAR_PALETTE = ['#16a34a','#0ea5e9','#8b5cf6','#f59e0b','#ef4444','#ec4899','#0891b2','#65a30d'];
  const pickAvatarColor = (name: string) => AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length];

  const relativeTime = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60_000);
    if (m < 60) return `Updated ${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `Updated ${h}h ago`;
    return `Updated ${Math.floor(h / 24)}d ago`;
  };

  type WsStatus = 'ACTIVE' | 'SHARED' | 'PRIVATE';
  const STATUS_CFG: Record<WsStatus, { bg: string; fg: string; dot: string }> = {
    ACTIVE:  { bg: '#dcfce7', fg: '#166534', dot: '#16a34a' },
    SHARED:  { bg: '#dbeafe', fg: '#1e40af', dot: '#3b82f6' },
    PRIVATE: { bg: '#f3f4f6', fg: '#374151', dot: '#6b7280' },
  };

  const allSorted = [...organizations].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );

  // Invariant: a user must always be linked to at least one workspace.
  // When this is the only workspace, the leave/delete action on the card is
  // hidden entirely (not just disabled) so it never reads as "missing" next
  // to other cards. Gated on `!loading` so the hidden state doesn't flash
  // during the initial fetch (where `organizations` is still the empty default).
  const isOnlyWorkspace = !loading && organizations.length <= 1;

  const q = orgSearch.toLowerCase().trim();
  const filtered = q ? allSorted.filter(o => o.name.toLowerCase().includes(q)) : allSorted;
  const pageSize = viewMode === 'grid' ? PAGE_SIZE : LIST_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(orgPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const goToPage = (p: number) => setOrgPage(Math.max(1, Math.min(p, totalPages)));

  // Build page number list with ellipsis
  const pageNumbers: (number | '...')[] = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (safePage > 3) pages.push('...');
    for (let i = Math.max(2, safePage - 1); i <= Math.min(totalPages - 1, safePage + 1); i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  })();

  return (
    <div>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '28px', fontWeight: 700, color: 'hsl(var(--foreground))', lineHeight: 1.2, margin: 0 }}>Organisations</h2>
          <p style={{ fontSize: '14px', color: 'hsl(var(--muted-foreground))', marginTop: '6px' }}>Manage your connected business entities and team organisations.</p>
        </div>
        <CreateOrganizationDialog onSuccess={handleWorkspaceCreated} buttonText='+ Create Organisation' tenantType='organisation' dialogTitle='Create Organisation' dialogDescription='Set up a new organisation for your team' />
      </div>

      {/* ── Toolbar: Search + view toggle ── */}
      {allSorted.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, maxWidth: '360px' }}>
            <svg
              style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'hsl(var(--muted-foreground))', pointerEvents: 'none' }}
              width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={orgSearch}
              onChange={e => { setOrgSearch(e.target.value); setOrgPage(1); }}
              placeholder="Search organisations…"
              style={{
                width: '100%', padding: '9px 12px 9px 36px', borderRadius: '10px',
                border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', fontSize: '13.5px',
                color: 'hsl(var(--foreground))', outline: 'none', boxSizing: 'border-box',
              }}
            />
            {orgSearch && (
              <button
                onClick={() => { setOrgSearch(''); setOrgPage(1); }}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--muted-foreground))', padding: '2px', lineHeight: 1 }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            )}
          </div>

          {/* Count */}
          <span style={{ fontSize: '12.5px', color: 'hsl(var(--muted-foreground))', whiteSpace: 'nowrap' }}>
            {q ? `${filtered.length} of ${allSorted.length}` : `${allSorted.length} total`}
          </span>

          {/* View toggle */}
          <div style={{ display: 'flex', alignItems: 'center', background: 'hsl(var(--muted))', borderRadius: '8px', padding: '3px', gap: '2px', marginLeft: 'auto' }}>
            <button
              onClick={() => { setViewMode('grid'); setOrgPage(1); }}
              title="Grid view"
              style={{
                padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                background: viewMode === 'grid' ? 'hsl(var(--card))' : 'transparent',
                color: viewMode === 'grid' ? '#111827' : '#9ca3af',
                boxShadow: viewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
              }}
            >
              <LayoutGrid size={15} />
            </button>
            <button
              onClick={() => { setViewMode('list'); setOrgPage(1); }}
              title="List view"
              style={{
                padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                background: viewMode === 'list' ? 'hsl(var(--card))' : 'transparent',
                color: viewMode === 'list' ? '#111827' : '#9ca3af',
                boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
              }}
            >
              <List size={15} />
            </button>
          </div>
        </div>
      )}

      {/* ── Empty state ── */}
      {filtered.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', color: 'hsl(var(--muted-foreground))', fontSize: '14px', background: 'hsl(var(--card))', borderRadius: '16px', border: '1px solid hsl(var(--border))' }}>
          {q ? `No organisations match "${orgSearch}".` : 'No organisations yet.'}
        </div>
      ) : viewMode === 'grid' ? (

        /* ── Grid view ── */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {paginated.map(org => {
            const isPersonal = org.tenant_type === 'personal';
            const isAdmin = org.role === 'admin' || org.role === 'org_admin';
            const status: WsStatus = isPersonal ? 'PRIVATE' : (isAdmin ? 'ACTIVE' : 'SHARED');
            const cfg = STATUS_CFG[status];
            const initial = org.name.charAt(0).toUpperCase();
            const avatarBg = pickAvatarColor(org.name);
            const memberCount = memberCounts[org.id] ?? 0;

            return (
              <div
                key={org.id}
                style={{ background: 'hsl(var(--card))', borderRadius: '16px', border: '1px solid hsl(var(--border))', padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px', transition: 'box-shadow 0.15s' }}
              >
                {/* Avatar + status badge row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '12px', background: logoUrls[org.id] ? '#ffffff' : avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: logoUrls[org.id] ? '1px solid hsl(var(--border))' : 'none', flexShrink: 0 }}>
                    {logoUrls[org.id] ? (
                      <img src={logoUrls[org.id]!} alt={org.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ color: '#fff', fontSize: '22px', fontWeight: 700 }}>{initial}</span>
                    )}
                  </div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: cfg.bg, color: cfg.fg, fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', padding: '3px 10px', borderRadius: '9999px' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                    {status}
                  </span>
                </div>

                {/* Name + meta */}
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '17px', fontWeight: 700, color: 'hsl(var(--foreground))', margin: '0 0 4px 0', lineHeight: 1.3 }}>{org.name}</h3>
                  <p style={{ fontSize: '12.5px', color: 'hsl(var(--muted-foreground))', margin: 0 }}>
                    {relativeTime(org.updated_at)} • {memberCount} {memberCount === 1 ? 'Member' : 'Members'}
                  </p>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <button
                    onClick={() => navigate(`/organisation/${org.id}`)}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', fontSize: '13px', fontWeight: 500, color: 'hsl(var(--muted-foreground))', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
                  >
                    <SettingsIcon size={14} />
                    Manage
                  </button>
                  {!isOnlyWorkspace && (
                    <button
                      onClick={() => handleLeaveOrganization(org.id, org.name)}
                      aria-label={isPersonal ? `Delete personal workspace ${org.name}` : `Leave organisation ${org.name}`}
                      title={isPersonal ? 'Delete personal workspace' : 'Leave organisation'}
                      style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'hsl(var(--card))', border: '1px solid #fecaca', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f87171', flexShrink: 0, transition: 'background 0.15s' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(239,68,68,0.12)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--card))'; }}
                    >
                      {isPersonal ? <Trash2 size={15} /> : <LogOut size={15} />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      ) : (

        /* ── List view ── */
        <div style={{ background: 'hsl(var(--card))', borderRadius: '16px', border: '1px solid hsl(var(--border))', overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px 140px', gap: '12px', padding: '10px 20px', background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Organisation</span>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Members</span>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</span>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'right' }}>Actions</span>
          </div>

          {paginated.map((org, idx) => {
            const isPersonal = org.tenant_type === 'personal';
            const isAdmin = org.role === 'admin' || org.role === 'org_admin';
            const status: WsStatus = isPersonal ? 'PRIVATE' : (isAdmin ? 'ACTIVE' : 'SHARED');
            const cfg = STATUS_CFG[status];
            const initial = org.name.charAt(0).toUpperCase();
            const avatarBg = pickAvatarColor(org.name);
            const memberCount = memberCounts[org.id] ?? 0;
            const isLast = idx === paginated.length - 1;

            return (
              <div
                key={org.id}
                style={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px 140px', gap: '12px', padding: '14px 20px', alignItems: 'center', borderBottom: isLast ? 'none' : '1px solid hsl(var(--border))', transition: 'background 0.12s' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                {/* Name + avatar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                  <div style={{ width: '38px', height: '38px', borderRadius: '9px', background: logoUrls[org.id] ? 'hsl(var(--card))' : avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: logoUrls[org.id] ? '1px solid hsl(var(--border))' : 'none', flexShrink: 0 }}>
                    {logoUrls[org.id] ? (
                      <img src={logoUrls[org.id]!} alt={org.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ color: '#fff', fontSize: '15px', fontWeight: 700 }}>{initial}</span>
                    )}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: '14px', fontWeight: 600, color: 'hsl(var(--foreground))', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{org.name}</p>
                    <p style={{ fontSize: '11.5px', color: 'hsl(var(--muted-foreground))', margin: 0 }}>{relativeTime(org.updated_at)}</p>
                  </div>
                </div>

                {/* Members */}
                <span style={{ fontSize: '13.5px', color: 'hsl(var(--muted-foreground))', fontWeight: 500 }}>
                  {memberCount} {memberCount === 1 ? 'member' : 'members'}
                </span>

                {/* Status badge */}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: cfg.bg, color: cfg.fg, fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', padding: '3px 10px', borderRadius: '9999px', width: 'fit-content' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                  {status}
                </span>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                  <button
                    onClick={() => navigate(`/organisation/${org.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '7px', background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', fontSize: '12.5px', fontWeight: 500, color: 'hsl(var(--muted-foreground))', cursor: 'pointer', transition: 'background 0.15s', whiteSpace: 'nowrap' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
                  >
                    <SettingsIcon size={12} />
                    Manage
                  </button>
                  {!isOnlyWorkspace && (
                    <button
                      onClick={() => handleLeaveOrganization(org.id, org.name)}
                      aria-label={isPersonal ? `Delete personal workspace ${org.name}` : `Leave organisation ${org.name}`}
                      title={isPersonal ? 'Delete personal workspace' : 'Leave organisation'}
                      style={{ width: '32px', height: '32px', borderRadius: '7px', background: 'hsl(var(--card))', border: '1px solid #fecaca', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f87171', flexShrink: 0, transition: 'background 0.15s' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(239,68,68,0.12)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--card))'; }}
                    >
                      {isPersonal ? <Trash2 size={13} /> : <LogOut size={13} />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px' }}>
          <span style={{ fontSize: '12.5px', color: 'hsl(var(--muted-foreground))' }}>
            Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {/* Prev */}
            <button
              onClick={() => goToPage(safePage - 1)}
              disabled={safePage === 1}
              style={{ width: '34px', height: '34px', borderRadius: '8px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', cursor: safePage === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: safePage === 1 ? 'hsl(var(--muted-foreground))' : 'hsl(var(--foreground))', transition: 'background 0.15s' }}
              onMouseEnter={e => { if (safePage !== 1) (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--card))'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>

            {/* Page numbers */}
            {pageNumbers.map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} style={{ width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', color: 'hsl(var(--muted-foreground))' }}>…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => goToPage(p as number)}
                  style={{
                    width: '34px', height: '34px', borderRadius: '8px', border: '1px solid',
                    borderColor: safePage === p ? '#33b864' : 'hsl(var(--border))',
                    background: safePage === p ? '#33b864' : 'hsl(var(--card))',
                    color: safePage === p ? '#fff' : 'hsl(var(--muted-foreground))',
                    fontSize: '13px', fontWeight: safePage === p ? 700 : 400,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (safePage !== p) (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
                  onMouseLeave={e => { if (safePage !== p) (e.currentTarget as HTMLElement).style.background = 'hsl(var(--card))'; }}
                >
                  {p}
                </button>
              )
            )}

            {/* Next */}
            <button
              onClick={() => goToPage(safePage + 1)}
              disabled={safePage === totalPages}
              style={{ width: '34px', height: '34px', borderRadius: '8px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', cursor: safePage === totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: safePage === totalPages ? 'hsl(var(--muted-foreground))' : 'hsl(var(--foreground))', transition: 'background 0.15s' }}
              onMouseEnter={e => { if (safePage !== totalPages) (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--card))'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Confirmation dialog (unchanged logic) ── */}
      {confirmLeave && (
        <div className='fixed inset-0 z-50 overflow-y-auto'>
          <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
            <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' onClick={() => setConfirmLeave(null)} />
            <div className='inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full'>
              <div className='bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4'>
                <div className='sm:flex sm:items-start'>
                  <div className='mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10'>
                    <AlertCircle className='h-6 w-6 text-red-600' />
                  </div>
                  <div className='mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left'>
                    <h3 className='text-lg leading-6 font-medium text-gray-900'>
                      {confirmLeave.tenantType === 'personal' ? 'Delete Personal Workspace' : 'Leave Organization'}
                    </h3>
                    <div className='mt-2'>
                      <p className='text-sm text-gray-500'>
                        {confirmLeave.tenantType === 'personal'
                          ? <> Are you sure you want to delete <strong>{confirmLeave.orgName}</strong>? This action cannot be undone.</>
                          : <> Are you sure you want to leave <strong>{confirmLeave.orgName}</strong>?</>
                        }
                      </p>
                      {confirmLeave.tenantType === 'organization' && <p className='text-sm text-gray-500 mt-2'>If you are the last member, the organization will be permanently deleted.</p>}
                      {confirmLeave.tenantType === 'personal' && <p className='text-sm text-gray-500 mt-2'>All data in this personal workspace will be permanently deleted.</p>}
                    </div>
                  </div>
                </div>
              </div>
              <div className='bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse'>
                <button type='button' onClick={confirmLeaveOrganization} disabled={leaving}
                  className='w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed'>
                  {leaving ? <><Loader2 className='w-4 h-4 mr-2 animate-spin' />{confirmLeave.tenantType === 'personal' ? 'Deleting...' : 'Leaving...'}</> : confirmLeave.tenantType === 'personal' ? 'Delete Personal Workspace' : 'Leave Organization'}
                </button>
                <button type='button' onClick={() => setConfirmLeave(null)} disabled={leaving}
                  className='mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed'>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Subscription Settings Component
const SubscriptionSettings: React.FC = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableAddonPacks, setAvailableAddonPacks] = useState<any[]>([]);
  const [subscriptionAddons, setSubscriptionAddons] = useState<any[]>([]);
  const [addingAddonId, setAddingAddonId] = useState<string | null>(null);
  const [addonQty, setAddonQty] = useState<Record<string, number>>({});
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [resourceUsage, setResourceUsage] = useState<ResourceUsage[]>([]);
  const [workspaces, setWorkspaces] = useState<Organization[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [workspaceSwitcherOpen, setWorkspaceSwitcherOpen] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadWorkspaces();
    }
  }, [user?.id]);

  const loadWorkspaces = async () => {
    try {
      const orgData = await getUserOrganizations();
      const mapped: Organization[] = orgData.organizations.map(org => {
        const membership = orgData.membership.find(m => m.organisation_id === org.id);
        return {
          id: org.id,
          name: org.name,
          description: org.description,
          tenant_type: org.tenant_type,
          created_at: org.created_at,
          created_by: org.created_by,
          updated_at: org.updated_at,
          role: membership?.role || 'member',
        };
      });
      setWorkspaces(mapped);
      if (mapped.length > 0) {
        setSelectedWorkspaceId(mapped[0].id);
      }
    } catch (err) {
      console.error('Error loading workspaces for subscription:', err);
    }
  };

  useEffect(() => {
    if (user?.id && selectedWorkspaceId) {
      loadSubscriptionData(selectedWorkspaceId);
    }
  }, [user?.id, selectedWorkspaceId]);

  const selectedWorkspace = workspaces.find(w => w.id === selectedWorkspaceId);

  const loadSubscriptionData = async (workspaceId?: string) => {
    try {
      setLoading(true);

      // Prefer org subscription (owner_type = 'organisation') since subscriptions
      // are assigned to the org during onboarding, not directly to the user
      let subData = null;

      const tenantId = workspaceId ?? selectedWorkspaceId;

      if (tenantId) {
        subData = await getOrganizationSubscription(tenantId);
      }
      // Fall back to user-level subscription if no org subscription found
      if (!subData) {
        subData = await getUserSubscription(user!.id);
      }

      // Load available plans
      const planData = await getAvailablePlans();
      setPlans(planData);

      // Join plan data onto the subscription record
      if (subData && subData.plan_id) {
        const matchedPlan = planData.find(
          (p: any) => p.id === subData!.plan_id
        );
        if (matchedPlan) {
          (subData as any).plan = matchedPlan;
        }
      }
      setSubscription(subData);

      // Load addon data and resource types
      const [addonPacks, resTypes] = await Promise.all([
        getAvailableAddonPacks(),
        getResourceTypes(),
      ]);
      setAvailableAddonPacks(addonPacks);
      setResourceTypes(resTypes);

      if (subData) {
        const [subAddons, usage] = await Promise.all([
          getSubscriptionAddons(subData.id),
          getResourceUsage(subData.id),
        ]);
        setSubscriptionAddons(subAddons);
        setResourceUsage(usage);
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load subscription data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddon = async (packId: string) => {
    if (!subscription) return;
    setAddingAddonId(packId);
    try {
      await addSubscriptionAddon(subscription.id, packId, 1);
      const updated = await getSubscriptionAddons(subscription.id);
      setSubscriptionAddons(updated);
      toast({ title: 'Success', description: 'Addon pack added successfully' });
    } catch (error) {
      console.error('Error adding addon:', error);
      toast({
        title: 'Error',
        description: 'Failed to add addon pack',
        variant: 'destructive',
      });
    } finally {
      setAddingAddonId(null);
    }
  };

  const formatPrice = (plan: Plan) => {
    const price = plan.price_monthly ?? plan.price_yearly ?? 0;
    return `$${price}`;
  };

  const formatPeriod = (plan: Plan) => {
    if ((plan.price_monthly ?? 0) === 0 && (plan.price_yearly ?? 0) === 0)
      return 'forever';
    return plan.price_yearly && !plan.price_monthly ? 'year' : 'month';
  };

  const getFeatures = (plan: Plan) => {
    if (!plan.features || typeof plan.features !== 'object') {
      return ['Basic features'];
    }

    const features: string[] = [];
    const f = plan.features;

    // Top-level flat fields (e.g. { contacts_count: 3 })
    if (f.contacts_count != null) features.push(`${f.contacts_count} Contacts`);
    if (f.users_count != null) features.push(`${f.users_count} Users`);
    if (f.storage_gb != null) features.push(`${f.storage_gb}GB Storage`);

    // Nested scopingai structure
    if (f.scopingai && typeof f.scopingai === 'object') {
      if (f.scopingai.proposals_limit)
        features.push(`${f.scopingai.proposals_limit} Proposals`);
      if (f.scopingai.advanced_templates) features.push('Advanced Templates');
    }

    // Nested knowledgebase structure
    if (f.knowledgebase && typeof f.knowledgebase === 'object') {
      if (f.knowledgebase.documents_limit)
        features.push(`${f.knowledgebase.documents_limit} Documents`);
    }

    return features.length > 0 ? features.slice(0, 4) : ['Basic features'];
  };

  if (loading) {
    return (
      <div className='dashboard-card p-6'>
        <div className='flex items-center justify-center py-8'>
          <Loader2 className='w-6 h-6 animate-spin' />
          <span className='ml-2'>Loading subscription data...</span>
        </div>
      </div>
    );
  }

  // Compute extra quantities per resource_type_id from active subscription addons
  const addonExtraById: Record<string, number> = {};
  for (const addon of subscriptionAddons) {
    if (addon.status !== 'active') continue;
    const pack = availableAddonPacks.find(
      (p: any) => p.id === addon.addon_pack_id
    );
    if (pack) {
      addonExtraById[pack.resource_type_id] =
        (addonExtraById[pack.resource_type_id] || 0) + pack.quantity;
    }
  }

  // Build usage items from plan features — resource type code == feature key
  const usageItems = (() => {
    const features = subscription?.plan?.features;
    if (!features || typeof features !== 'object') return [];

    const idToType = new Map(
      resourceTypes.map((rt: ResourceType) => [rt.id, rt])
    );
    const codeToId = new Map(
      resourceTypes.map((rt: ResourceType) => [rt.code, rt.id])
    );
    const usageByTypeId = new Map(
      resourceUsage.map((u: ResourceUsage) => [
        u.resource_type_id,
        u.current_usage,
      ])
    );

    return Object.entries(features)
      .filter(([code, val]) => {
        const rtId = codeToId.get(code);
        const rt = rtId ? idToType.get(rtId) : undefined;
        // Only show count-type resources (or unknown types that have a numeric value)
        const isCount = !rt || !rt.value_type || rt.value_type === 'count';
        return isCount && typeof val === 'number' && (val as number) > 0;
      })
      .map(([code, baseLimit]) => {
        const rtId = codeToId.get(code);
        const rt = rtId ? idToType.get(rtId) : undefined;
        const extra = rtId ? addonExtraById[rtId] || 0 : 0;
        const used = rtId ? (usageByTypeId.get(rtId) ?? 0) : 0;
        return {
          label: rt?.name ?? code,
          used,
          total: (baseLimit as number) + extra,
          additional: extra,
        };
      });
  })();

  return (
    <div className='space-y-6'>
      {/* Workspace Switcher */}
      {workspaces.length > 0 && (
        <div className='dashboard-card p-4'>
          <div className='flex items-center gap-3'>
            <div className='p-2 bg-primary/10 rounded-lg shrink-0'>
              <Building className='w-4 h-4 text-primary' />
            </div>
            <div className='flex-1 min-w-0'>
              <p className='text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1'>Viewing subscription for</p>
              <Popover open={workspaceSwitcherOpen} onOpenChange={setWorkspaceSwitcherOpen}>
                <PopoverTrigger asChild>
                  <button className='flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors'>
                    <span className='truncate'>{selectedWorkspace?.name ?? 'Select organisation'}</span>
                    <span className='inline-flex items-center gap-1 text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full shrink-0'>
                      {selectedWorkspace?.tenant_type === 'personal' ? 'Personal' : 'Organisation'}
                    </span>
                    <ChevronRight className='w-3.5 h-3.5 text-muted-foreground rotate-90 shrink-0' />
                  </button>
                </PopoverTrigger>
                <PopoverContent align='start' className='p-1.5 w-64'>
                  <p className='text-[11px] font-medium text-muted-foreground px-2 pt-1 pb-1.5'>Switch organisation</p>
                  {workspaces.map(ws => (
                    <button
                      key={ws.id}
                      onClick={() => { setSelectedWorkspaceId(ws.id); setWorkspaceSwitcherOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left text-sm transition-colors ${ws.id === selectedWorkspaceId ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-foreground'}`}
                    >
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${ws.id === selectedWorkspaceId ? 'bg-primary/20' : 'bg-muted'}`}>
                        {ws.tenant_type === 'personal' ? <User size={13} className={ws.id === selectedWorkspaceId ? 'text-primary' : 'text-muted-foreground'} /> : <Building size={13} className={ws.id === selectedWorkspaceId ? 'text-primary' : 'text-muted-foreground'} />}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <span className='block font-medium truncate'>{ws.name}</span>
                        <span className='block text-[11px] text-muted-foreground capitalize'>{ws.tenant_type === 'personal' ? 'Personal' : 'Organisation'}</span>
                      </div>
                      {ws.id === selectedWorkspaceId && <Check size={13} className='text-primary shrink-0' />}
                    </button>
                  ))}
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      )}

      {/* Usage */}
      {subscription && (
        <div className='dashboard-card p-6'>
          <div className='flex items-center justify-between mb-4'>
            <h3 className='text-xl font-semibold'>Usage</h3>
            <button
              onClick={() => loadSubscriptionData()}
              className='text-sm text-primary hover:underline flex items-center gap-1'
            >
              Refresh usage
            </button>
          </div>
          {usageItems.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No usage data available for your current plan.
            </p>
          ) : (
            <div className='space-y-4'>
              {usageItems.map(item => {
                const pct =
                  item.total > 0
                    ? Math.round((item.used / item.total) * 100)
                    : 0;
                return (
                  <div key={item.label}>
                    <div className='flex justify-between text-sm mb-1'>
                      <div className='flex items-center gap-2'>
                        <span className='font-medium'>{item.label}</span>
                        {item.additional > 0 && (
                          <span className='inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700'>
                            +{item.additional} from addons
                          </span>
                        )}
                      </div>
                      <span className='text-muted-foreground'>
                        {item.used}/{item.total} — {item.total - item.used}{' '}
                        remaining, {pct}% used
                      </span>
                    </div>
                    <div className='h-2 bg-muted rounded-full overflow-hidden'>
                      <div
                        className='h-full bg-primary rounded-full transition-all'
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Current Plan */}
      {subscription && (
        <div className='dashboard-card p-6'>
          <h3 className='text-xl font-semibold mb-4'>Current Plan</h3>
          <div className='flex items-center justify-between p-4 bg-primary/5 border border-primary/20 rounded-lg'>
            <div className='flex items-center space-x-4'>
              <div className='p-3 bg-primary/10 rounded-lg'>
                <Crown className='w-6 h-6 text-primary' />
              </div>
              <div>
                <div className='flex items-center gap-2'>
                  <h4 className='font-semibold'>
                    {subscription.plan?.name || 'Unknown Plan'}
                  </h4>
                  <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'>
                    • Active
                  </span>
                </div>
                <p className='text-sm text-muted-foreground'>
                  {subscription.plan ? formatPrice(subscription.plan) : '$0'}/
                  {subscription.plan
                    ? formatPeriod(subscription.plan)
                    : 'forever'}
                  {subscription.billing_cycle
                    ? ` • Billed ${subscription.billing_cycle}ly`
                    : ''}
                </p>
              </div>
            </div>
            {subscription.current_period_end && (
              <div className='text-right'>
                <p className='text-sm text-muted-foreground'>Next billing</p>
                <p className='font-medium'>
                  {new Date(
                    subscription.current_period_end
                  ).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Available Plans */}
      <div className='dashboard-card p-6'>
        <h3 className='text-xl font-semibold mb-6'>Available Plans</h3>
        {plans.length === 0 ? (
          <div className='text-center py-8 text-muted-foreground'>
            <AlertCircle className='w-12 h-12 mx-auto mb-4 opacity-50' />
            <p>No plans available at the moment.</p>
          </div>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
            {plans.map(plan => {
              const isCurrentPlan = subscription?.plan_id === plan.id;
              return (
                <div
                  key={plan.id}
                  className={`border rounded-lg p-6 ${
                    isCurrentPlan
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  } transition-colors`}
                >
                  <div className='text-center'>
                    <h4 className='font-semibold text-lg'>{plan.name}</h4>
                    <div className='mt-2'>
                      <span className='text-3xl font-bold'>
                        {formatPrice(plan)}
                      </span>
                      <span className='text-muted-foreground'>
                        /{formatPeriod(plan)}
                      </span>
                    </div>
                    {plan.description && (
                      <p className='text-sm text-muted-foreground mt-2'>
                        {plan.description}
                      </p>
                    )}
                  </div>
                  <ul className='mt-6 space-y-3'>
                    {getFeatures(plan).map((feature, idx) => (
                      <li key={idx} className='flex items-center space-x-2'>
                        <Check className='w-4 h-4 text-green-500' />
                        <span className='text-sm'>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    className={`w-full mt-6 py-2 rounded-lg transition-colors ${
                      isCurrentPlan
                        ? 'bg-muted text-muted-foreground cursor-not-allowed'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90'
                    }`}
                    disabled={isCurrentPlan}
                  >
                    {isCurrentPlan ? 'Current Plan' : 'Upgrade'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add-on Packs */}
      {availableAddonPacks.length > 0 && (
        <div className='dashboard-card p-6'>
          <h3 className='text-xl font-semibold mb-4'>Add-on Packs</h3>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {availableAddonPacks.map(pack => {
              const alreadyAdded = subscriptionAddons.some(
                (a: any) => a.addon_pack_id === pack.id && a.status === 'active'
              );
              return (
                <div
                  key={pack.id}
                  className='border border-border rounded-lg p-4 flex flex-col gap-2'
                >
                  <div>
                    <h4 className='font-medium'>{pack.name}</h4>
                    {pack.description && (
                      <p className='text-sm text-muted-foreground'>
                        {pack.description}
                      </p>
                    )}
                  </div>
                  <div className='flex items-center justify-between text-sm'>
                    <span className='font-medium text-primary'>
                      +{pack.quantity} added to your limit
                    </span>
                    <span className='text-muted-foreground'>
                      {pack.price != null && pack.price > 0
                        ? `$${pack.price}`
                        : 'Free'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleAddAddon(pack.id)}
                    disabled={
                      alreadyAdded || addingAddonId === pack.id || !subscription
                    }
                    className={`w-full py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      alreadyAdded
                        ? 'bg-muted text-muted-foreground cursor-not-allowed'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90'
                    }`}
                  >
                    {addingAddonId === pack.id ? (
                      <span className='flex items-center justify-center gap-1'>
                        <Loader2 className='w-3 h-3 animate-spin' /> Adding...
                      </span>
                    ) : alreadyAdded ? (
                      '✓ Added'
                    ) : (
                      '+ Add Pack'
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Billing History */}
      {invoices.length > 0 && (
        <div className='dashboard-card p-6'>
          <h3 className='text-xl font-semibold mb-4'>Billing History</h3>
          <div className='space-y-3'>
            {invoices.map(invoice => (
              <div
                key={invoice.id}
                className='flex items-center justify-between py-3 border-b border-border last:border-b-0'
              >
                <div>
                  <p className='font-medium'>
                    {new Date(invoice.invoice_date).toLocaleDateString()}
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    {subscription?.plan?.name || 'Subscription'}
                  </p>
                </div>
                <div className='text-right'>
                  <p className='font-medium'>
                    {(invoice.amount / 100).toLocaleString('en-US', {
                      style: 'currency',
                      currency: invoice.currency.toUpperCase(),
                    })}
                  </p>
                  <p
                    className={`text-sm ${
                      invoice.status === 'paid'
                        ? 'text-green-600'
                        : 'text-orange-600'
                    }`}
                  >
                    {invoice.status.charAt(0).toUpperCase() +
                      invoice.status.slice(1)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
